import React, { useContext, useEffect, useRef, useState } from "react";

import { pnStatusCheckApi } from "../../../../network/Apiservices";
import { AccountType, transferFormType } from "../../../home/types";
import { Text } from "../../design-system/Text";
import ApproveTotpTransfer from "./ApproveTotpTransfer";
import ApproveSTotpTransfer from "./ApproveStotpTransfer";
import { AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ApiContext from "../../../../network/KeyExchangeWrapper";
import axiosInstance from "../../../../network/axiosInstance";
import { initToast } from "../../../../network/serverErrorHandler";
import useTailwindBreakpoints from "../../../../hooks/useTailwindBreakPoints";
import { Img } from "../../design-system/Img";
import { useTheme } from "../../../../theme/ThemeProvider";
import { apiServiceHeadersConfig } from "../../../../constants/config";

interface PropsType {
  showModal: boolean;
  toggleApprove: () => void;
  transferData?: any;
  setStatus: (status: transferFormType["approvalStatus"]) => void;
  setErrorCode?: (errorCode: string) => void;
  fromAccount?: AccountType | null;
  from?: string;
  portfolioSubmitHandler?: (otp?: string, manual?: boolean) => void;
}

const defaultState = {
  totp: "",
  totpError: "",
  isTotpVisible: false,
};
const ApproveTransfer: React.FC<PropsType> = ({
  showModal,
  toggleApprove,
  transferData,
  setStatus,
  setErrorCode,
  from,
  portfolioSubmitHandler,
}) => {
  const navigate = useNavigate();
  const [state, setState] = useState(defaultState);
  const context = useContext(ApiContext);
  const { isSecureChannelEnabled } = context || {};
  const interval = useRef<NodeJS.Timeout | null>(null);
  const breakPoints = useTailwindBreakpoints();
  const { theme } = useTheme();
  useEffect(() => {
    if (transferData.mfaPayload?.weight === "SMS") {
      setState({ ...state, isTotpVisible: true });
      return;
    } else if (transferData.isDeviceListModelOpen) {
      return;
    } else {
      interval.current = setInterval(() => {
        statusApi();
      }, 3000);
    }
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [state.isTotpVisible, transferData.mfaPayload]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearIntervals = () => {
    if (interval.current) {
      clearInterval(interval.current);
    }
  };

  const statusApi = async () => {
    try {
      if (!transferData.mfaPayload?.transactionId) {
        return;
      }
      let response = await pnStatusCheckApi(
        transferData.mfaPayload.transactionId
      );
      if (from === "home") {
        if (response.status === "INITIATED") {
          return;
        }
        if (response.status === "FAILED") {
          setStatus("FAILED");

          initToast("Authentication failed. Please try Login again.");
          logout();
          if (setErrorCode) setErrorCode("");
        } else if (response.status === "DECLINED") {
          setStatus("DECLINED");
          clearIntervals();
          initToast("Authentication Declined. App will Logout.");
          logout();
          if (setErrorCode) setErrorCode("");
        } else if (response.status === "AUTHORIZED") {
          setStatus("AUTHORIZED");
          clearIntervals();
          if (setErrorCode) setErrorCode("");

          if (portfolioSubmitHandler) portfolioSubmitHandler(undefined, false);
        }
      } else {
        if (response.status === "INITIATED") {
          return;
        }
        if (response.status === "FAILED") {
          setStatus("FAILED");
          clearIntervals();
          if (setErrorCode) setErrorCode("");
        } else {
          console.log("response_STATUS:", response.status);
          clearIntervals();
          setStatus(response.status);
          if (setErrorCode) setErrorCode("");
        }
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const logout = () => {
    localStorage.setItem("user", "null");
    sessionStorage.clear();
    axiosInstance.defaults.headers.common.Authorization = undefined;
    axiosInstance.defaults.headers.common[apiServiceHeadersConfig.secureSessionId] = "";
    navigate("/auth/login");
  };

  const toggleTotp = () => {
    setState({ ...state, isTotpVisible: !state.isTotpVisible });
  };

  const navigateToResult = (
    status: transferFormType["approvalStatus"],
    errCode?: string
  ) => {
    setStatus(status);
    if (!errCode) {
      setErrorCode && setErrorCode("");
    } else {
      setErrorCode && setErrorCode(errCode);
    }
  };

  if (!showModal) return null;

  return (
    <>
      <div
        className="justify-center items-center flex overflow-x-hidden 
      fixed inset-0 z-50 outline-none focus:outline-none"
      >
        <div className="relative w-auto mx-auto">
          {/*content*/}
          <div
            className="border-0 rounded-[24px] shadow-lg relative flex flex-col 
          bg-white-A700 outline-none focus:outline-none  
          overflow-hidden "
          >
            {/*header*/}
            <div className="flex flex-row items-center justify-between p-4  bg-accountHeader rounded-t">
              <p className="xs:text-[0.9em] sm:text-[0.9em] lg:text-[20px] font-normal  text-white-A700 m-0">
                {transferData.mfaPayload.title ?? "Approve Transaction"}
              </p>
              {breakPoints.lg ? (
                <Text
                  className="text-[13px] font-medium leading-6 text-white-A700 hover:cursor-pointer"
                  onClick={toggleApprove}
                >
                  Cancel
                </Text>
              ) : (
                <Img
                  className="hover:cursor-pointer"
                  src={"x-close.svg"}
                  onClick={toggleApprove}
                />
              )}
            </div>
            {/*body*/}
            <AnimatePresence>
              {!state.isTotpVisible && (
                <ApproveSTotpTransfer
                  breakPoints={breakPoints}
                  from={from}
                  toggleTotp={toggleTotp}
                  theme={theme}
                />
              )}
              {state.isTotpVisible && (
                <ApproveTotpTransfer
                  toggleTotp={toggleTotp}
                  transferData={transferData}
                  navigateToResultModal={navigateToResult}
                  portfolioSubmitHandler={portfolioSubmitHandler}
                  isSecureChannelEnabled={isSecureChannelEnabled}
                  breakPoints={breakPoints}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <button
        className="opacity-25 fixed inset-0 z-40 bg-black-800"
        onClick={toggleApprove}
        onKeyDown={toggleApprove}
      ></button>
    </>
  );
};

export default ApproveTransfer;
